import type { DistributorLogo } from 'fym-shared/src/smartlinks/distributorLogos';

export interface LogoSize {
  width: number;
  height: number;
}

/** The square tile of the design: 32 px on a card, 44 px in the modal head. */
export const markTileSize = (isLarge: boolean): number => (isLarge ? 44 : 32);

/**
 * The fixed box of a mark in a list (a Catalog card, the confirmation). Every
 * mark sits in the middle of the same box, so the names after the marks start
 * on one vertical line. The box is wider than the tile because a word logo
 * needs the width; a square mark keeps its 32 px tile in the middle.
 */
export const MARK_FRAME_WIDTH = 64;

/** The free space around a logo inside its tile. */
const MARK_INSET = 4;
const MARK_INSET_LARGE = 5;

/** The free space at the sides of a word logo inside the fixed box. */
const WORDMARK_FRAME_INSET = 6;

/** A word logo keeps a readable letter height and a width cap. */
const WORDMARK_HEIGHT = 12;
const WORDMARK_HEIGHT_LARGE = 16;
const WORDMARK_MAX_WIDTH = MARK_FRAME_WIDTH - 2 * WORDMARK_FRAME_INSET;
const WORDMARK_MAX_WIDTH_LARGE = 100;

const fitBox = (box: number, aspectRatio: number): LogoSize =>
  aspectRatio >= 1
    ? { width: box, height: Math.round((box / aspectRatio) * 10) / 10 }
    : { width: Math.round(box * aspectRatio * 10) / 10, height: box };

/**
 * The drawn size of a distributor logo. A compact mark fits the square tile
 * with an inset (a full-bleed mark fills it). A word logo keeps its letter
 * height up to the width cap. On a card the cap is the fixed box, so the word
 * logo is contained and the plate never grows past the box. In the modal head
 * (one mark, no list) the plate still grows around the logo.
 */
export const distributorLogoSize = (logo: DistributorLogo, isLarge: boolean): LogoSize => {
  const tile = markTileSize(isLarge);
  if (logo.isFullBleed) {
    return { width: tile, height: tile };
  }
  if (logo.shape === 'mark') {
    return fitBox(tile - 2 * (isLarge ? MARK_INSET_LARGE : MARK_INSET), logo.aspectRatio);
  }
  const height = isLarge ? WORDMARK_HEIGHT_LARGE : WORDMARK_HEIGHT;
  const maxWidth = isLarge ? WORDMARK_MAX_WIDTH_LARGE : WORDMARK_MAX_WIDTH;
  const width = height * logo.aspectRatio;
  if (width <= maxWidth) {
    return { width: Math.round(width * 10) / 10, height };
  }
  return { width: maxWidth, height: Math.round((maxWidth / logo.aspectRatio) * 10) / 10 };
};
